import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { kioskApi } from '../lib/kiosk-api.js';

// Cash-drawer denominations + money formatter (used only by this view).
const CASH_DENOMS = [
  { key: '100',     label: '$100 bill',  value: 100 },
  { key: '50',      label: '$50 bill',   value:  50 },
  { key: '20',      label: '$20 bill',   value:  20 },
  { key: '10',      label: '$10 bill',   value:  10 },
  { key: '5',       label: '$5 bill',    value:   5 },
  { key: '2',       label: '$2 bill',    value:   2 },
  { key: '1',       label: '$1 bill',    value:   1 },
  { key: 'quarter', label: 'Quarter',    value: 0.25, sub: '25¢' },
  { key: 'dime',    label: 'Dime',       value: 0.10, sub: '10¢' },
  { key: 'nickel',  label: 'Nickel',     value: 0.05, sub: '5¢'  },
  { key: 'penny',   label: 'Penny',      value: 0.01, sub: '1¢'  },
];
function fmtUSD(n) { return '$' + (Math.round((Number(n) || 0) * 100) / 100).toFixed(2); }

// Draft persistence — counting a drawer means not touching the iPad, so an idle
// logout mid-count used to lose every entry. Persist the in-progress quantities
// to localStorage keyed by store + business day; they survive a logout and are
// restored on the next PIN-in, then cleared once the count is submitted.
function draftKey(locKey, date) { return `monarch_cashdraft_${locKey}_${date}`; }
function readDraft(locKey, date) {
  try {
    const raw = localStorage.getItem(draftKey(locKey, date));
    if (!raw) return null;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length === CASH_DENOMS.length) return arr.map((n) => Number(n) || 0);
  } catch { /* corrupt/absent draft — start fresh */ }
  return null;
}
function writeDraft(locKey, date, qty) {
  try {
    if (qty.some((q) => q > 0)) localStorage.setItem(draftKey(locKey, date), JSON.stringify(qty));
    else localStorage.removeItem(draftKey(locKey, date)); // never persist an all-zero draft
  } catch { /* storage full/blocked — non-fatal */ }
}
function clearDraft(locKey, date) { try { localStorage.removeItem(draftKey(locKey, date)); } catch { /* ok */ } }

export function KioskCashCloseView({ session }) {
  const locKey = session.location.key;
  const [state, setState] = useState(null); // { status, submitted_total, opening_float_dollars, business_date }
  const [qty, setQty] = useState(CASH_DENOMS.map(() => 0));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function load() {
    setErr('');
    try {
      const d = await kioskApi('GET', `/api/kiosk/${locKey}/cash-count/today`, null, session.token);
      setState(d);
      if (d.status !== 'draft') {
        setQty(CASH_DENOMS.map(() => 0)); // already submitted — nothing to restore
      } else {
        // Restore an in-progress count from a prior (possibly logged-out) session.
        const saved = readDraft(locKey, d.business_date);
        if (saved) setQty(saved);
      }
    } catch (e) { setErr(e.message || String(e)); }
  }
  useEffect(() => { load(); }, []);

  const total = qty.reduce((s, q, i) => s + q * CASH_DENOMS[i].value, 0);

  // Persist every keystroke so a logout mid-count never loses the drawer.
  useEffect(() => {
    if (state?.status === 'draft' && state.business_date) writeDraft(locKey, state.business_date, qty);
  }, [qty, state?.status, state?.business_date, locKey]);

  async function submit() {
    if (busy) return;
    if (total === 0 && !confirm('Total is $0.00 — submit anyway?')) return;
    setBusy(true); setErr('');
    try {
      const denominations = {};
      CASH_DENOMS.forEach((d, i) => { if (qty[i] > 0) denominations[d.key] = qty[i]; });
      await kioskApi('POST', `/api/kiosk/${locKey}/cash-count/today`,
        { denominations, drawer_total: Math.round(total * 100) / 100 },
        session.token);
      if (state?.business_date) clearDraft(locKey, state.business_date); // submitted — draft no longer needed
      await load();
    } catch (e) { setErr(e.message || String(e)); }
    finally { setBusy(false); }
  }

  // D104: the error branch must precede the !state gate. load() sets err on
  // failure but leaves state null, so the gate below used to win and the err
  // card ~44 lines down was unreachable — the same defect as the Board (D97).
  // Worst possible place for it: this is the drawer count at close, on a
  // shared iPad, so the spinner stranded someone with counted cash.
  if (!state && err) {
    return (
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#b91c1c' }}>Couldn’t load the cash count</div>
        <div style={{ background: '#fee', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 12.5 }}>{err}</div>
        <div style={{ fontSize: 12, color: '#616161' }}>
          Your counted amounts are saved on this iPad — nothing is lost. Try again, and tell a manager if it keeps failing.
        </div>
        <button onClick={load} style={{ alignSelf: 'flex-start', padding: '8px 14px', fontSize: 13, fontWeight: 700,
          background: '#1B2B1B', color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer' }}>Try again</button>
      </div>
    );
  }
  if (!state) return <div style={{ fontSize: 13, color: '#9e9e9e', padding: 16 }}>Loading…</div>;

  // Already submitted (pending or certified) — locked confirmation screen.
  if (state.status !== 'draft') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ background: state.status === 'certified' ? '#e6f2ec' : '#fbf7ec',
                       border: `1px solid ${state.status === 'certified' ? '#a8d3bb' : '#eee2c4'}`,
                       borderRadius: 12, padding: 20, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>{state.status === 'certified' ? '✅' : '🕒'}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: state.status === 'certified' ? '#226b4a' : '#8a6b1a' }}>
            {state.status === 'certified' ? 'Certified for today' : 'Submitted — awaiting manager review'}
          </div>
          <div style={{ fontSize: 13, color: '#5a5a5a', marginTop: 6 }}>
            Amount you submitted: <strong>{fmtUSD(state.submitted_total)}</strong>
          </div>
          <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 4 }}>
            {session.location.name} · {state.business_date}
          </div>
        </div>
        <div style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
          If you need to correct this count, ask a manager to reopen it.
        </div>
      </div>
    );
  }

  // Draft — the count form.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 640, margin: '0 auto', width: '100%' }}>
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{session.location.name}</div>
            <div style={{ fontSize: 11, color: '#9e9e9e' }}>Close-out count · {state.business_date}</div>
          </div>
          {state.opening_float_dollars != null && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: 0.6 }}>Leave in drawer</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#2f5d50' }}>{fmtUSD(state.opening_float_dollars)}</div>
            </div>
          )}
        </div>
      </div>
      {err && <div style={{ background: '#fee', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 12.5 }}>{err}</div>}

      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #eee', color: '#9e9e9e', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              <th style={{ textAlign: 'left', padding: '8px 6px' }}>Denomination</th>
              <th style={{ textAlign: 'right', padding: '8px 6px' }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '8px 6px' }}>Row total</th>
            </tr>
          </thead>
          <tbody>
            {CASH_DENOMS.map((d, i) => (
              <tr key={d.key} style={{ borderBottom: '1px solid #fafafa' }}>
                <td style={{ padding: '8px 6px', fontWeight: 600 }}>
                  {d.label}
                  {d.sub && <div style={{ fontSize: 11, color: '#9e9e9e', fontWeight: 400 }}>{d.sub}</div>}
                </td>
                <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                  <input type="number" inputMode="numeric" min="0" step="1"
                    value={qty[i] || ''} placeholder="0"
                    onChange={(e) => {
                      const v = Math.max(0, Math.floor(Number(e.target.value) || 0));
                      setQty((cur) => { const nx = [...cur]; nx[i] = v; return nx; });
                    }}
                    style={{ width: 80, padding: '10px 8px', fontSize: 17, textAlign: 'right',
                              border: '1px solid #d0d4d9', borderRadius: 6, background: '#fdfcf7', fontVariantNumeric: 'tabular-nums' }} />
                </td>
                <td style={{ padding: '8px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums',
                              color: qty[i] * d.value === 0 ? '#bbb' : '#212121', fontWeight: 500, minWidth: 90 }}>
                  {fmtUSD(qty[i] * d.value)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid #b48a47', background: '#f6f4ee' }}>
              <td style={{ padding: '14px 6px', fontSize: 12, color: '#6b6b6b',
                            textTransform: 'uppercase', letterSpacing: 0.6 }}>Drop in safe (total)</td>
              <td></td>
              <td style={{ padding: '14px 6px', textAlign: 'right', fontSize: 22,
                            fontWeight: 800, color: '#2f5d50', fontVariantNumeric: 'tabular-nums' }}>
                {fmtUSD(total)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <button onClick={submit} disabled={busy}
        style={{ background: '#2f5d50', color: '#fff', border: 'none', borderRadius: 10,
                  padding: '16px 20px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
                  opacity: busy ? 0.6 : 1 }}>
        {busy ? 'Submitting…' : 'Submit for manager review'}
      </button>
    </div>
  );
}
